import { SerializedGraphicAnnotation } from 'pdftron/docManager/AnnotationTools/GraphicZone';
import { DocumentToast } from 'components/DocumentToast/DocumentToast';
import { CtfImagesResult, CtfMatchResult } from 'store/request/match/types';
import { SerializedCropZoneAnnotation } from 'pdftron/docManager/AnnotationTools/CropZone';
import { PolygonZoneAnnotation } from 'pdftron/docManager/AnnotationTools/PolygonZone';
import {
  type BoundingBox,
  ColorId,
  Dictionaries,
  DifferenceModule,
  SortDifferencesOption,
  type SortByPerModule,
  GraphicsOptionKey,
  GraphicsSensitivity,
  MatchingMode,
} from '@verify/shared-utils';
export { SortDifferencesOption } from '@verify/shared-utils';

export enum DocumentStates {
  INITIAL = 'INITIAL',
  LOADING = 'LOADING',
  LOADED = 'LOADED',
}

export enum DocumentTextProcessing {
  INITIAL = 'INITIAL',
  COMPLETED = 'COMPLETED',
}
export interface PersistedDifferenceComment {
  diffIds: string[];
  comment: string;
}

export interface DifferenceViewOptions {
  differenceTypeFilters: { [index: string]: boolean };
  differenceStatusFilters: { [index: string]: boolean };
  sortBy?: SortDifferencesOption;
  /** Per-module sort overrides. Missing entries fall back to the inspection-wide sortBy. */
  sortByPerModule?: SortByPerModule;
}

export type DifferenceViewOptionsPayload = Partial<DifferenceViewOptions> & {
  forceVisibleDifferenceId?: string;
  replaceSortByPerModule?: boolean;
};

export interface Quad {
  getPoints(): any;
  toRect(): any;
  x1: number;
  x2: number;
  x3: number;
  x4: number;
  y1: number;
  y2: number;
  y3: number;
  y4: number;
}

export interface InputZone {
  number: number;
  quads: Quad[];
  usedTool?: PDFTronToolsDBVals;
  annotationId: string;
  // Text zones confirmed via Confirm All are grouped for navigation; persist this id
  groupId?: string;
}

export interface Crossout {
  number: number;
  quads: Quad[];
}

export interface RectangularCrop {
  cropId?: string;
  pageNumber: number;
  x1: number;
  x2: number;
  y1: number;
  y2: number;
}

export interface PolygonCrop {
  cropId?: string;
  pageNumber: number;
  points: [number, number][];
}

export type Crop = RectangularCrop | PolygonCrop;
export type ResyncZone = Crop;

export interface InputPage {
  number: number;
  included: boolean;
  underline?: Quad[][] | null;
  crossout?: Crossout[] | null;
  crop?: Crop[] | null;
  zone?: InputZone[] | null;
}

export interface ManualPageRotation {
  angle: number;
  pageNumber: number;
}
export enum PdfBoxType {
  cropBox = 'CropBox',
  bleedBox = 'BleedBox',
  trimBox = 'TrimBox',
  artBox = 'ArtBox',
}

export type CtfPdfBox = {
  pageNumber: number;
  boundingBox: BoundingBox;
};

export enum PDFTronBoxType {
  'CropBox' = 'e_crop',
  'MediaBox' = 'e_media',
  'BleedBox' = 'e_bleed',
  'TrimBox' = 'e_trim',
  'ArtBox' = 'e_art',
}

export interface SamplingPoint {
  pageNumber: number;
  point: [number, number];
}

export interface InputDocument {
  pages: InputPage[];
  graphicZones?: SerializedGraphicAnnotation[];
  cropZones?: SerializedCropZoneAnnotation[];
  manualAlignZones?: SerializedManualAlignZone[];
  manualPageRotations?: ManualPageRotation[];
  layers?: Layer[];
  separations?: Separation[];
  polygons?: Polygons[DocumentTypes];
  type: 'master' | 'sample';
  overprint?: boolean;
  samplingPoints?: SamplingPoint[];
}

export interface InputDocuments {
  documents: InputDocument[];
}

export interface SearchedAnnotation {
  annotationId: string;
  text: string;
  zoneId: number;
}

export interface SelectedText {
  annotationId: string;
  text: string;
  zoneId: string;
  selected: boolean;
  hasResult: boolean;
  searchedAnnotations: Array<SearchedAnnotation>;
}

export interface Layer {
  name: string;
  visible: boolean;
  children?: Layer[];
}

export interface Separation {
  name: string;
  enabled: boolean;
  rgb: number[];
}

export interface DocumentData {
  state: DocumentStates.INITIAL | DocumentStates.LOADING | DocumentStates.LOADED;
  pageRange: Array<number>;
  // perhaps this should be renamed to inputAnnotations instead of annotations, representing input tool annotations
  annotations: Array<InputAnnotation>;
  liveText: boolean;
  documentTextProcessing: DocumentTextProcessing.INITIAL | DocumentTextProcessing.COMPLETED;
  zoneSelectedTexts: Array<SelectedText>;
  pages: { [key: number]: PageState };
  totalPageCount: number;
  currentPage: number;
  layers?: Array<Layer>;
  separations?: Array<Separation>;
  overprint?: boolean;
}

export enum PDFTronTools {
  PAN = 'Pan',
  UNDERLINE = 'AnnotationCreateTextUnderline',
  CROSSOUT = 'AnnotationCreateTextStrikeout',
  ZONE = 'TextZoneCreateTool',
  HIGHLIGHT = 'AnnotationCreateTextHighlight',
  GRAPHIC = 'GraphicCreateTool',
  MARQUEE_ZONE = 'MarqueeZoneCreateTool',
  SHIFTED_GRAPHIC = 'ShiftedGraphicCreateTool',
  SCALED_GRAPHIC = 'ScaledGraphicCreateTool',
  CROP = 'CropTool',
  POLYGON = 'PolygonTool',
  MARKUP = 'MarkupTool',
  RESYNC = 'ResyncTool',
  MERGE_GRAPHICS_ZONE = 'MergeGraphicsZone',
  COLOR_PICKER = 'ColorPickerTool',
}

export enum PDFTronToolsDBVals {
  PAN = 'PAN',
  UNDERLINE = 'UNDERLINE',
  CROSSOUT = 'CROSSOUT',
  ZONE = 'ZONE',
  HIGHLIGHT = 'HIGHLIGHT',
  GRAPHIC = 'GRAPHIC',
  MARQUEE_ZONE = 'MARQUEE_ZONE',
}

export const PDFTRON_DEFAULT_TOOL = PDFTronTools.PAN;

export enum PDFTronZoomTools {
  MARQUEE = 'MarqueeZoomTool',
}

export type GraphicTool = PDFTronTools.GRAPHIC;

export type CropTool = PDFTronTools.CROP;

export type PolygonTool = PDFTronTools.POLYGON;

export type MarkupTool = PDFTronTools.MARKUP;

export type ResyncTool = PDFTronTools.RESYNC;

export type MergeGraphicsZoneTool = PDFTronTools.MERGE_GRAPHICS_ZONE;

export type ColorPickerTool = PDFTronTools.COLOR_PICKER;

export type TextTool =
  | PDFTronTools.PAN
  | PDFTronTools.UNDERLINE
  | PDFTronTools.CROSSOUT
  | PDFTronTools.ZONE
  | PDFTronTools.HIGHLIGHT
  | PDFTronTools.MARQUEE_ZONE
  | PDFTronTools.SHIFTED_GRAPHIC
  | PDFTronTools.SCALED_GRAPHIC;

export type PDFTool =
  | TextTool
  | GraphicTool
  | CropTool
  | PolygonTool
  | MergeGraphicsZoneTool
  | MarkupTool
  | ResyncTool
  | ColorPickerTool;

export enum DocumentTypes {
  source = 'source',
  target = 'target',
}

export enum ReportDocumentTypes {
  report = 'report',
}

export type AllDocumentTypes = DocumentTypes | ReportDocumentTypes;

export const OPPOSITE_DOCUMENT = {
  [DocumentTypes.source]: DocumentTypes.target,
  [DocumentTypes.target]: DocumentTypes.source,
  [ReportDocumentTypes.report]: ReportDocumentTypes.report,
};

export enum PageRangeOption {
  ALL = 'all',
  RANGE = 'range',
}

export type DocumentType = DocumentTypes.source | DocumentTypes.target;

export interface InputAnnotation {
  annotationId: string;
  usedTool?: PDFTool;
  page: number;
  quads?: Quad[];
  graphicZone?: SerializedGraphicAnnotation;
  cropZone?: SerializedCropZoneAnnotation | PolygonZoneAnnotation;
  manualAlignZone?: SerializedManualAlignZone;
  samplingPoint?: SamplingPoint;
  number?: number;
  // Persist group information so grouped text zones are restored on reload
  groupId?: string;
}

export enum GVAnnotationType {
  excludedPage = 'Excluded_Grey_Out',
}

export enum AnnotationVisibilityOptions {
  hidden = 'hidden',
  placeholder = 'placeholder', // same properties as a visible annotation but hidden by default
  visible = 'visible',
}

export interface Inspection {
  id: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  inspectionStatus: InspectionStatuses;
}

export enum CTFModuleTypes {
  text = 'Text',
  textMatch = 'TextMatch',
  spelling = 'Spelling',
  graphics = 'Graphics',
  barcode = 'Barcode',
  braille = 'Braille',
  fontMetrics = 'FontMetrics',
  color = 'Color',
  markup = 'Markup',
}

export const CTF_MODULE_TO_DIFFERENCE_MODULE: Record<CTFModuleTypes, DifferenceModule> = {
  [CTFModuleTypes.text]: DifferenceModule.text,
  [CTFModuleTypes.textMatch]: DifferenceModule.textMatch,
  [CTFModuleTypes.spelling]: DifferenceModule.spelling,
  [CTFModuleTypes.graphics]: DifferenceModule.graphics,
  [CTFModuleTypes.barcode]: DifferenceModule.barcode,
  [CTFModuleTypes.braille]: DifferenceModule.braille,
  [CTFModuleTypes.fontMetrics]: DifferenceModule.fontMetrics,
  [CTFModuleTypes.color]: DifferenceModule.color,
  [CTFModuleTypes.markup]: DifferenceModule.markup,
};

interface CTFJobRawResults {
  matches?: CtfMatchResult[];
  images?: CtfImagesResult;
  unmatchedCropIds?: string[];
  metadata?: {
    ctfVersion?: string;
    textMatchCdnPathPrefix?: string;
    textMatchMetadata?: {
      masterPages: Record<string, number>;
      samplePages: Record<string, number>;
      totalMatches: number;
    };
  };
}

interface CTFJob {
  id: string;
  type: CTFModuleTypes;
  rawResults: CTFJobRawResults;
  status: InspectionStatuses;
  error?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface Job {
  id: string;
  inspectionId: string;
  input?: InputDocuments;
  createdAt: string;
  metadata: JobMetaData;
  ctfJobs: CTFJob[];
}

export interface JobMetaData {
  errors?: JobErrors;
  isBarcode: boolean;
  isBraille?: boolean;
  isFullPageGraphics: boolean;
  isEmbeddedImages?: boolean;
}

export interface JobErrors {
  spelling?: string;
  barcodes?: string;
  text2?: string;
  graphics?: string;
}

export interface ShowLibrary {
  [DocumentTypes.source]: boolean;
  [DocumentTypes.target]: boolean;
}

export interface ZoomOptionState {
  [ReportDocumentTypes.report]: '' | PageWidthFit;
  [DocumentTypes.source]: '' | PageWidthFit;
  [DocumentTypes.target]: '' | PageWidthFit;
}

export interface InspectionSettings {
  tableEnhancement?: boolean;
  dictionaryName?: Dictionaries | Dictionaries[];
  spelling?: boolean;
  fullPageGraphics?: boolean;
  barcode?: boolean;
  barcodeSettings?: BarcodeSettings;
  brailleLanguage?: string;
  singleFile?: boolean;
  text?: boolean;
  textMatchingMode?: MatchingMode;
  qrdMode?: boolean;
  extractEmbeddedImages?: boolean;
  enableLENFileMode?: boolean;
  checkResultsAgainstAnnotations?: boolean;
  graphics?: GraphicsOptions;
  fontSizeCheckValue?: number | null;
  colorCheck?: boolean;
  highlightColor?: ColorId;
  isSharing?: boolean;
  resyncCount?: number; // number of resyncs performed on the inspection
  hasShownResyncToolToast?: boolean; // whether the resync tool toast has been shown
  cumulativeNetResyncedDifferences?: number; // cumulative net differences added/removed by resyncs
  originalOrgId?: string; // contains the org id of the original inspection
  isImported?: boolean;
  passingGrade?: string;
  groupId?: string; // ID of the linked inspection group this inspection belongs to
}

export interface AutoMatchGraphic {
  autoMatchFail?: number;
  autoMatchPass?: number;
}

export type GraphicsOptionValue = GraphicsSensitivity | MatchingMode | string; // string = custom profile id

export interface GraphicsOptions {
  [GraphicsOptionKey.Sensitivity]?: GraphicsSensitivity | string;
  [GraphicsOptionKey.MatchingMode]?: MatchingMode;
}

export enum BarcodeSettingKey {
  CustomProfileId = 'customProfileId',
  CustomProfileName = 'customProfileName',
}

export type BarcodeSettingValue = string; // 'Default' or custom profile id/name

export interface BarcodeSettings {
  [BarcodeSettingKey.CustomProfileId]?: string;
  [BarcodeSettingKey.CustomProfileName]?: string;
}

export interface InspectionState {
  source: DocumentData;
  target: DocumentData;
  report: DocumentData;
  selectedTool: PDFTool;
  differenceViewOptions: DifferenceViewOptions;
  differencesLoaded: boolean;
  focusedDifferenceId: string;
  previousDifferenceId: string;
  persistedDifferenceComments: Record<string, string>;
  syncScrolling: boolean;
  noResultAnnotationId: string;
  invalidScaledGraphicZoneRatio: boolean;
  unconfirmTextZoneError: boolean;
  unconfirmedGraphicZoneError: boolean;
  unconfirmedMarqueeZoneError: boolean;
  changeToolError: string;
  pendingCropZone: { documentType: DocumentTypes | null; pendingCropId: string };
  activePolygonCreation: {
    isActive: boolean;
    documentType: DocumentTypes | null;
    pageNumber: number | null;
    currentPage: number | null;
  };
  pendingShapeRequest: {
    fileId: string;
    documentType: DocumentTypes;
    layerOrSeparationName: string;
    type: 'Layer' | 'Separation';
  } | null;
  loadingInspection: boolean;
  name: string;
  inspectionId: string;
  userId?: string;
  inspectionStatus: InspectionStatuses | '';
  manualSelectedZoneId: number;
  manualSelectedAnnotationId: string;
  lastJobId: string | null;
  lastJobInput: InputDocuments | null;
  lastJobCtfVersion: string | null;
  lastJobCreatedAt?: string | null;
  ctfJobs: (CTFModuleTypes | 'All')[];
  textMatchJobId: string | null;
  textMatchCdnPathPrefix: string | null;
  textMatchMetadata: {
    masterPages: Record<string, number>;
    samplePages: Record<string, number>;
    totalMatches: number;
  } | null;
  lastJobMetadata?: JobMetaData;
  selectingZone: boolean;
  openPanel: boolean;
  pageRangeOption: PageRangeOption;
  showLibrary: ShowLibrary;
  internalAnnotationsVisibility: boolean;
  internalAnnotationsVisibilityDefault: boolean;
  tableEnhancementEnabledState: boolean;
  nextTextZoneId: number;
  nextGraphicZoneId: number;
  nextCropZoneId: number;
  nextMarqueeZoneId: number;
  nextCrossoutZoneId: number;
  nextColorPointId: number;
  nextManualAlignZoneId: number;
  settings: InspectionSettings;
  autoMatchGraphic: AutoMatchGraphic;
  customDictionaryIDs: string[];
  updatedAt: string;
  differencePanelOpen: boolean;
  changeGridDirection: string;
  changeGridWidth: number | null;
  // REFACTOR: should be moved out of inspection store to somewhere more relevant
  shiftedGraphicRefId: string;
  searchingForGraphicZone: boolean;
  isFilesLoaded: boolean;
  reloadingDifferences: boolean;
  resyncStatus: {
    loading: boolean;
    processingResults: boolean;
    cancelled: boolean;
    undoingResync: boolean;
  };
  isBarcode: boolean;
  brailleLanguage: string;
  // Error: inspection did not complete
  // all: every page was unmatched
  // none: no pages were unmatched
  unmatchedPages: number[];
  unmatchedCropPages: number[]; // Pages with unmatched crop zones
  isResultsPanelHidden: boolean;
  flashMode: boolean;
  lastJobMatches: CtfMatchResult[];
  lastJobImages?: CtfImagesResult;
  lastJobUnmatchedCropIds?: string[];
  polygons: Polygons;
  manualAlignMode: {
    isActive: boolean;
    cropZoneId: string | null;
    cropZonePageNumber: number | null;
    originZoneDocumentType: DocumentTypes | null;
    masterAnchorPoint: { x: number; y: number; pageNumber: number } | null;
    targetAnchorPoint: { x: number; y: number; pageNumber: number } | null;
  };
}

export type Polygons = {
  [key in DocumentTypes]?: {
    points: [number, number][][][] | '';
    key?: string;
    layerOrSeparationName?: string;
    type?: 'Layer' | 'Separation' | '';
  };
};

export interface PageState {
  thumbnail: string;
  loaded: boolean;
  fullImage?: string;
}

export enum PageWidthFit {
  fitPage = 'FitPage',
  fitWidth = 'FitWidth',
}
export interface StartInspectionData {
  noLiveText: boolean;
}

export enum InspectionStatuses {
  idle = 'PENDING',
  queued = 'QUEUED',
  inprogress = 'IN_PROGRESS',
  completed = 'COMPLETE',
  error = 'ERROR',
  finalizing = 'FINALIZING',
}

export enum AutoPrepJobStatus {
  completed = 'COMPLETE',
  queued = 'QUEUED',
  error = 'ERROR',
  inprogress = 'IN_PROGRESS',
  pending = 'PENDING',
  cancelled = 'CANCELLED',
}

export enum ZoomLevelLimits {
  // values represent zoom % / 100 with the way pdftron handles numbered zoom values
  maxZoom = 99.99,
  minZoom = 0.05,
  defaultZoom = 1,
  differenceMax = 7.5,
}

export enum ContentDifferences {
  Insertion = 'Insertion',
  Deletion = 'Deletion',
  Change = 'Change',
  Case = 'Case',
  Space = 'Space',
  ReadingOrder = 'ReadingOrder',
  Hyphen = 'Hyphen',
  UnmatchedAnnotation = 'UnmatchedAnnotation',
  EmbeddedImage = 'EmbeddedImage',
}

export enum FormattingDifferences {
  Bold = 'Bold',
  Italic = 'Italic',
  Underlined = 'Underlined',
  Subscript = 'Subscript',
  Superscript = 'Superscript',
  FontType = 'FontType',
  FontColor = 'FontColor',
  Size = 'Size',
  FontSize = 'FontSize',
  SoftHyphen = 'SoftHyphen',
  LineBreakSpace = 'LineBreakSpace',
}

export enum SpellingDifferences {
  SpellingGeneral = 'SpellingGeneral',
  Abbreviation = 'Abbreviation',
  ProperNoun = 'ProperNoun',
  Alphanumeric = 'Alphanumeric',
}

export enum CheckDifferences {
  FontMetrics = 'FontMetrics',
}

export enum DifferenceTypeNames {
  Insertion = 'Insertion',
  Deletion = 'Deletion',
  Change = 'Change',
  Space = 'Space',
  Case = 'Case',
  Hyphen = 'Hyphen',
  Bold = 'Bold',
  Italic = 'Italic',
  Size = 'Size',
  FontSize = 'FontSize',
  Underlined = 'Underlined',
  Subscript = 'Subscript',
  Superscript = 'Superscript',
  FontType = 'FontType',
  FontColor = 'FontColor',
  SoftHyphen = 'SoftHyphen',
  LineBreakSpace = 'LineBreakSpace',
  ReadingOrder = 'ReadingOrder',
  SpellingGeneral = 'SpellingGeneral',
  Abbreviation = 'Abbreviation',
  ProperNoun = 'ProperNoun',
  Alphanumeric = 'Alphanumeric',
  Barcode = 'Barcode',
  Braille = 'Braille',
  Graphics = 'Graphics',
  EmbeddedImage = 'EmbeddedImage',
  UnmatchedAnnotation = 'UnmatchedAnnotation',
  FontMetrics = 'FontMetrics',
  Markup = 'Markup',
  Color = 'Color',
}

const TYPE_TO_DISPLAY_NAME: { [key in keyof typeof DifferenceTypeNames]?: string } = {
  FontType: 'Font Type',
  FontSize: 'Font Size',
  Size: 'Font Size',
  FontColor: 'Font Color',
  Underlined: 'Underline',
  Subscript: 'Sub/Super',
  Superscript: 'Sub/Super',
  LineBreakSpace: 'Line Break',
  SoftHyphen: 'Soft Hyphen',
  Hyphen: 'Change',
  ReadingOrder: 'Reading Order',
  ProperNoun: 'Proper Noun',
  SpellingGeneral: 'General',
  Alphanumeric: 'Alphanumeric',
  EmbeddedImage: 'Image Detected',
  UnmatchedAnnotation: 'Unmatched Annotation',
  FontMetrics: 'Font Size Check',
  Markup: 'Markups',
  Color: 'Color',
};

export const getDifferenceDisplayName = (type: DifferenceTypeNames) => {
  return TYPE_TO_DISPLAY_NAME[type] || type;
};

export interface UpdateInspectionData {
  input?: InputDocuments;
  name?: string;
  sortOptions?: {
    sortBy?: SortDifferencesOption;
    sortOrder?: string;
    sortByPerModule?: SortByPerModule;
  };
  filterOptions?: { [key: string]: boolean };
  status?: InspectionStatuses;
  masterFileId?: string | null;
  sampleFileId?: string | null;
  lastJobId?: string | null;
  settings?: InspectionSettings;
  autoMatchGraphic?: AutoMatchGraphic;
  customDictionaryIDS?: string[];
}

export enum ZoneCustomData {
  zoneId = 'zoneId',
  confirmed = 'confirmed',
  zoneAnnotation = 'zoneAnnotation',
  zoneSearch = 'zoneSearch',
  noResult = 'noResult',
  zoneGroup = 'zoneGroup',
  nonSelectable = 'nonSelectable',
}

export enum MarqueeZoneCustomData {
  zoneId = 'zoneId',
  marqueeZoneAnotation = 'marqueeZoneAnotation',
  confirmed = 'confirmed',
  nonSelectable = 'nonSelectable',
}

export enum CropZoneCustomData {
  cropZoneAnnotation = 'cropZoneAnnotation',
}

export enum ManualAlignZoneCustomData {
  manualAlignZoneAnnotation = 'manualAlignZoneAnnotation',
  linkedZoneId = 'linkedZoneId',
  originZoneDocumentType = 'originZoneDocumentType',
  zoneNumber = 'manualAlignZoneNumber',
  resized = 'manualAlignZoneResized',
  positionSnapshot = 'manualAlignPositionSnapshot',
  aspectRatioUnlocked = 'manualAlignAspectRatioUnlocked',
}

export interface SerializedManualAlignZone {
  annotationId: string;
  pageNumber: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  anchorPoint: { x: number; y: number };
  linkedZoneId: string;
  originZoneDocumentType?: DocumentTypes;
  zoneNumber?: number;
  resized?: boolean;
  aspectRatioUnlocked?: boolean;
}

export enum PolygonZoneCustomData {
  polygonAnnotation = 'polygonAnnotation',
  shapePolygon = 'shapePolygon',
}

export enum GraphicZoneCustomData {
  graphicAnnotation = 'graphicAnnotation',
  zoneNumber = 'zoneNumber',
  confirmed = 'graphicConfirmed',
  readOnly = 'readOnly',
  shifted = 'shifted',
  scaled = 'scaled',
  matchedGraphicsAnnotation = 'matchedGraphicsAnnotation',
  autoGraphicsAnnotations = 'autoGraphicsAnnotations',
  rotationIndex = 'rotationIndex',
}

export enum ColorPickerCustomData {
  colorPickerAnnotation = 'colorPickerAnnotation',
  colorPointId = 'colorPointId',
  sampleX = 'sampleX',
  sampleY = 'sampleY',
}

export enum InspectedAnnotationCustomData {
  inspectedAnnotation = 'inspectedAnnotation',
}

export enum ShiftedHighlightCustomData {
  overlayAnnotation = 'overlayAnnotation',
  windowAnnotation = 'windowAnnotation',
  refAnnotationIds = 'refAnnotationIds',
}

export enum RepeatAnnotationCustomData {
  repeatAnnotation = 'repeatAnnotation',
}

export enum AnnotationCustomData {
  zoneNumber = 'zoneNumber',
  strikeoutAnnotation = 'strikeoutAnnotation',
  drawMarkups = 'drawMarkups',
  difference = 'difference',
  liveText = 'liveText',
  highContrast = 'highContrast',
  refAnnotationIds = 'refAnnotationIds',
  deletionMarker = 'deletionMarker',
  noDiffBox = 'noDiffBox',
  hiddenOverlay = 'hiddenOverlay',
  hiddenByHideOtherDifferences = 'hiddenByHideOtherDifferences',
  textHighlights = 'textHighlights',
  matchIndex = 'matchIndex',
  embeddedImageId = 'embeddedImageId',
}

export enum MarkupZoneCustomData {
  markupZoneAnnotation = 'markupZoneAnnotation',
}

export enum GVAnnotationCustomData {
  // identifying annotations we create
  gvAnnotation = 'gvAnnotation',
}

export type ZoomLockLevel = {
  [key in AllDocumentTypes]: number | PageWidthFit | null;
};

export interface Group {
  id: string;
  inspectionId: string;
  createdAt: string;
  deletedAt: string;
  updatedAt: string;
  differenceList: string[];
  comment: string;
  excluded: boolean;
  viewed: boolean;
  smartDiscard: boolean;
  statusId: string | null;
}

export interface GridCharacter {
  index: number;
  character: string;
  diff: boolean;
}
export interface Grids {
  masterGrid: GridCharacter[];
  sampleGrid: GridCharacter[];
}

export enum GridTypes {
  Change = 'Change',
  Hyphen = 'Hyphen',
  Space = 'Space',
  Case = 'Case',
}

export type DocumentToastState = {
  [key in DocumentTypes]: DocumentToast | null;
};

export interface AnnotationComment {
  author: string;
  content: string;
  updatedAt: Date;
}
